import React, { useState } from 'react';
import logo from '../../assets/images/logo.png';
import logoBranca from '../../assets/images/logo-branca.png';
import { Box, Typography, Drawer, IconButton, Tooltip, Collapse, useTheme, alpha } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';

// Icons
import SettingsIcon from '@mui/icons-material/Settings';
import RateReviewIcon from '@mui/icons-material/RateReview';
import ForumIcon from '@mui/icons-material/Forum';
import BuildIcon from '@mui/icons-material/Build';
import SnippetFolderIcon from '@mui/icons-material/SnippetFolder';
import HomeIcon from '@mui/icons-material/Home';
import BarChartIcon from '@mui/icons-material/BarChart';
import LocalDiningIcon from '@mui/icons-material/LocalDining';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import KitchenIcon from '@mui/icons-material/Kitchen';
import CancelIcon from '@mui/icons-material/Cancel';
import CampaignIcon from '@mui/icons-material/Campaign';
import CloseIcon from '@mui/icons-material/Close';
import CalculateIcon from '@mui/icons-material/Calculate';
import HistoryIcon from '@mui/icons-material/History';
import InsertChartIcon from '@mui/icons-material/InsertChart';
import GroupIcon from '@mui/icons-material/Group';
import StorageIcon from '@mui/icons-material/Storage';
import AutoAwesome from '@mui/icons-material/AutoAwesome';
import SummarizeIcon from '@mui/icons-material/Summarize';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import DescriptionIcon from '@mui/icons-material/Description';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import PriceCheckIcon from '@mui/icons-material/PriceCheck';
import CategoryIcon from '@mui/icons-material/Category';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import FlagIcon from '@mui/icons-material/Flag';
import SchoolIcon from '@mui/icons-material/School';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import Groups2Icon from '@mui/icons-material/Groups2';

// BRAND CONSTANTS
const BRAND_YELLOW = "#FDB913";
const MUTED_GRAY = "#999999";
const HOVER_BG = (theme) => alpha(theme.palette.text.primary, 0.05);
const ACTIVE_BG = (theme) => theme.palette.mode === 'dark' ? "rgba(141, 64, 36, 0.15)" : "rgba(141, 64, 36, 0.08)";

export default function IFoodSidebar({ open = true, onClose, isMobile = false, collapsed = false, onCollapse }) {
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const { auth } = React.useContext(AuthContext);
    const roleId = auth?.roleId;
    const isAdmin = Number(roleId) === 1;

    const isActive = (path) => location.pathname === path || (path !== '/home' && location.pathname.startsWith(path));
    const isChildActive = (item) => item.children?.some(child => isActive(child.path));

    const handleMenuClick = (path) => {
        if (!path) return;
        navigate(path);
        if (isMobile && onClose) onClose();
    };

    const handleCollapse = () => {
        if (onCollapse) onCollapse();
        window.dispatchEvent(new Event("resize"));
    };

    const sidebarWidth = collapsed ? '72px' : '260px';

    const MenuItem = ({ item, level = 0 }) => {
        const hasChildren = item.children && item.children.length > 0;
        // Filter children based on permissions too
        const visibleChildren = hasChildren ? item.children.filter(child => {
            if (isAdmin) return true;
            if (child.adminOnly) return false; // Legacy check, prefer permission
            if (child.permission && !auth?.user?.permissions?.includes(child.permission)) return false;
            return true;
        }) : [];

        const actuallyHasChildren = visibleChildren.length > 0;

        // If item has children but none are visible, hide parent? 
        // Logic handled in parent map/filter

        const active = isActive(item.path) || (actuallyHasChildren && isChildActive(item));
        const [open, setOpen] = useState(actuallyHasChildren && isChildActive(item));
        const Icon = item.icon;

        const handleClick = () => {
            if (actuallyHasChildren) {
                setOpen(!open);
            } else {
                handleMenuClick(item.path);
            }
        };

        return (
            <>
                <Tooltip title={collapsed ? item.label : ""} placement="right">
                    <Box
                        onClick={handleClick}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            pl: collapsed ? 0 : (3 + level),
                            pr: collapsed ? 0 : 2,
                            justifyContent: collapsed ? 'center' : 'flex-start',
                            py: 1.5, // Increased padding for rhythm
                            cursor: 'pointer',

                            // Color Logic
                            color: active ? 'primary.main' : 'text.secondary',
                            bgcolor: active ? ACTIVE_BG(theme) : 'transparent',

                            // Visual Indicators
                            borderLeft: active ? (theme) => `3px solid ${theme.palette.primary.main}` : '3px solid transparent', // Left Border Standard

                            transition: 'all 0.2s ease-in-out',

                            '&:hover': {
                                bgcolor: HOVER_BG(theme),
                                color: 'text.primary',
                                '& .MuiSvgIcon-root': {
                                    color: active ? 'primary.main' : 'text.primary'
                                }
                            },
                        }}
                    >
                        {Icon && (
                            <Icon sx={{
                                fontSize: 20,
                                // Icon is PRIMARY if active, otherwise secondary text
                                color: active ? 'primary.main' : 'text.secondary',
                                transition: 'color 0.2s',
                                ml: collapsed ? 0 : level
                            }} />
                        )}
                        {!collapsed && (
                            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Typography sx={{
                                    fontSize: '14px', // Standard Size
                                    fontFamily: 'Inter, sans-serif',
                                    fontWeight: active ? 600 : 500, // Medium to SemiBold
                                    letterSpacing: '-0.01em'
                                }}>
                                    {item.label}
                                </Typography>
                                {actuallyHasChildren && (open ? <ExpandLess fontSize="small" sx={{ opacity: 0.5 }} /> : <ExpandMore fontSize="small" sx={{ opacity: 0.5 }} />)}
                            </Box>
                        )}
                    </Box>
                </Tooltip>

                {actuallyHasChildren && !collapsed && (
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            {visibleChildren.map(child => (
                                <MenuItem key={child.path} item={child} level={level + 1} />
                            ))}
                        </Box>
                    </Collapse>
                )}
            </>
        );
    };

    const MenuGroup = ({ title, items }) => {
        // Filter items based on permissions
        const visibleItems = items.filter(item => {
            if (isAdmin) return true;
            if (item.adminOnly) return false;
            // Check item permission
            if (item.permission && !auth?.user?.permissions?.includes(item.permission)) return false;

            // If item has children, check if at least one child is visible
            if (item.children) {
                const visibleChildren = item.children.filter(child => {
                    if (isAdmin) return true;
                    if (child.adminOnly) return false;
                    if (child.permission && !auth?.user?.permissions?.includes(child.permission)) return false;
                    return true;
                });
                return visibleChildren.length > 0;
            }
            return true;
        });

        if (visibleItems.length === 0) return null;

        return (
            <Box sx={{ mb: 2, mt: 1 }}>
                {!collapsed && title && (
                    <Box sx={{ px: 3, py: 1 }}>
                        <Typography sx={{
                            fontSize: '11px',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: '1.5px',
                            color: MUTED_GRAY,
                            userSelect: 'none',
                            cursor: 'default' // Non-clickable
                        }}>
                            {title}
                        </Typography>
                    </Box>
                )}
                <Box>
                    {visibleItems.map((item, idx) => <MenuItem key={item.path || idx} item={item} />)}
                </Box>
            </Box>
        );
    };

    // Define Groups with Permissions
    const MENU_GROUPS = [
        {
            title: "Visão Geral",
            items: [
                { label: 'Início', icon: HomeIcon, path: '/home', permission: 'view_inicio' },
                // Hub com abas que reune os 5 dashboards do grupo "Análises" abaixo.
                { label: 'Desempenho', icon: BarChartIcon, path: '/desempenho', permission: 'view_desempenho' },
                { label: 'Saúde Estratégica', icon: InsertChartIcon, path: '/health', permission: 'view_health' },
                { label: 'Consultor IA', icon: AutoAwesome, path: '/advisor', permission: 'view_advisor' },
            ]
        },
        {
            // Sistema à parte: lê a réplica do caixa da loja (bi_vochica),
            // não o vochica_db nem o scraping da Teknisa.
            title: "PDV (direto do caixa)",
            items: [
                { label: 'Operação', icon: PointOfSaleIcon, path: '/pdv/operacao', permission: 'view_pdv_operacao' },
                { label: 'Análise', icon: InsertChartIcon, path: '/pdv/analise', permission: 'view_pdv_analise' },
                { label: 'Recorrência Buffet', icon: CreditCardIcon, path: '/recorrencia-buffet', permission: 'view_recorrencia_buffet' },
            ]
        },
        {
            title: "Análises",
            items: [
                { label: 'Delivery', icon: ShowChartIcon, path: '/delivery', permission: 'view_delivery' },
                { label: 'Pratos & Produtos', icon: LocalDiningIcon, path: '/pratos', permission: 'view_pratos' },
                { label: 'Marketing', icon: CampaignIcon, path: '/marketing', permission: 'view_marketing' },
                { label: 'Avaliações', icon: RateReviewIcon, path: '/marketing/avaliacoes', permission: 'view_avaliacoes' },
                { label: 'Entrevistas', icon: ForumIcon, path: '/entrevistas', permission: 'view_entrevistas' },
                { label: 'Logística', icon: LocalShippingIcon, path: '/logistica', permission: 'view_logistica' },
            ]
        },
        {
            title: "Cozinha & Produção",
            items: [
                { label: 'Relatório Operacional', icon: SummarizeIcon, path: '/operacional/relatorio', permission: 'view_relatorio_op' },
                {
                    label: 'Checklists',
                    icon: AssignmentTurnedInIcon,
                    children: [
                        { label: 'Diário', icon: AssignmentIcon, path: '/operacional/checklists', permission: 'view_checklists' },
                        { label: 'Coordenação', icon: AssignmentTurnedInIcon, path: '/operacional/checklist-coordenacao', permission: 'view_checklists' },
                    ]
                },
            ]
        },
        {
            title: "Estoque & Movimentações",
            items: [
                { label: 'Pedidos e Estoque', icon: ShoppingCartIcon, path: '/pedidos', permission: 'view_pedidos' },
                { label: 'Entrada', icon: ArrowDownwardIcon, path: '/entrada', permission: 'view_entrada' },
                { label: 'Saída', icon: ArrowUpwardIcon, path: '/saida', permission: 'view_saida' },
                { label: 'Faltas', icon: CancelIcon, path: '/faltas', permission: 'view_faltas' },
            ]
        },
        {
            title: "Cardápio & Custos",
            items: [
                // Mesma permissao de "Custos dos Pratos": a pagina embute aquela tela numa aba.
                { label: 'Fichas, Custos e CMV', icon: RestaurantMenuIcon, path: '/fichas-custos', permission: 'view_dre_custos' },
                { label: 'Custos dos Pratos', icon: PriceCheckIcon, path: '/operacional/produtos', permission: 'view_dre_custos' },
                // Biblioteca dos HTMLs de /public/docs (CMV, fichas tecnicas, POP, precificacao, viabilidade).
                { label: 'Documentos Técnicos', icon: DescriptionIcon, path: '/documentos' },
                {
                    label: 'Eng. Cardápio',
                    icon: MenuBookIcon,
                    children: [
                        { label: 'Painel', icon: InsertChartIcon, path: '/cardapio/painel', permission: 'view_cardapio_painel' },
                        { label: 'Insumos', icon: Inventory2Icon, path: '/cardapio/insumos', permission: 'view_cardapio_insumos' },
                        { label: 'Categorias', icon: CategoryIcon, path: '/cardapio/categorias', permission: 'view_cardapio_insumos' },
                        { label: 'Receitas Base', icon: KitchenIcon, path: '/cardapio/receitas', permission: 'view_cardapio_receitas' },
                        { label: 'Pratos/Ficha', icon: LocalDiningIcon, path: '/cardapio/pratos-ficha', permission: 'view_cardapio_pratos' },
                        { label: 'Canais', icon: LocalShippingIcon, path: '/cardapio/canais', permission: 'view_cardapio_canais' },
                        { label: 'Precificação', icon: AttachMoneyIcon, path: '/cardapio/precificacao', permission: 'view_cardapio_precifica' }
                    ]
                }
            ]
        },
        {
            title: "Financeiro",
            items: [
                { label: 'Faturamento', icon: AttachMoneyIcon, path: '/faturamento', permission: 'view_financeiro' },
                {
                    label: 'DRE',
                    icon: CalculateIcon,
                    children: [
                        { label: 'Relatório', icon: InsertChartIcon, path: '/dre', permission: 'view_dre_relatorio' },
                        { label: 'Lançamento', icon: BuildIcon, path: '/financeiro/dre/lancamento', permission: 'edit_dre_lancamento' },
                    ]
                },
                {
                    label: 'Fluxo de Caixa',
                    icon: InsertChartIcon,
                    children: [
                        { label: 'Painel', icon: BarChartIcon, path: '/financeiro/fluxo-caixa', permission: 'view_fluxo_caixa' },
                        { label: 'Lançamento', icon: BuildIcon, path: '/financeiro/fluxo-caixa/novo', permission: 'edit_fluxo_caixa' },
                    ]
                },
                { label: 'Metas', icon: FlagIcon, path: '/metas', permission: 'view_metas' },
            ]
        },
        {
            title: "Gente & Formação",
            items: [
                { label: 'Formação de Coordenadores', icon: SchoolIcon, path: '/formacao' },
                { label: 'Avaliação Mensal', icon: WorkspacePremiumIcon, path: '/formacao/avaliacao-mensal' },
                // Página única com abas: manual do colaborador (HTML e PDF) e do coordenador.
                { label: 'Gestão de Pessoas', icon: Groups2Icon, path: '/gestao-pessoas' },
            ]
        },
    ];

    const ADMIN_GROUP = {
        title: "Administração",
        items: [
            { label: 'Usuários', icon: GroupIcon, path: '/admin/users', permission: 'admin_users' },
            { label: 'Permissões', icon: BuildIcon, path: '/admin/roles', permission: 'admin_roles' },
            { label: 'Explorador DB', icon: StorageIcon, path: '/admin/database', permission: 'admin_db' },
            { label: 'Monitor', icon: BarChartIcon, path: '/admin/monitor', permission: 'admin_monitor' },
            { label: 'Logs', icon: HistoryIcon, path: '/admin/audit', permission: 'admin_logs' },
            { label: 'Anexos (Drive)', icon: SnippetFolderIcon, path: '/anexos', permission: 'admin_anexos' },
            { label: 'Designer de Gráficos', icon: InsertChartIcon, path: '/bi/designer', adminOnly: true },
        ]
    };

    const activeGroups = [...MENU_GROUPS, ADMIN_GROUP];

    const sidebarContent = (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'transparent', overflowX: 'hidden' }}>
            {/* Header / Logo */}
            <Box sx={{
                py: 3, // 24px vertical padding
                px: collapsed ? 1 : 3,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                // Removed fixed height, let padding drive it, but verify alignment
                minHeight: 80,
                borderBottom: '1px solid',
                borderColor: 'divider',
                position: 'relative' // For absolute positioning of toggle
            }}>
                <img
                    src={theme.palette.mode === 'dark' ? logoBranca : logo}
                    alt="Vó Chica"
                    style={{
                        width: 'auto', // Preserve aspect ratio
                        maxWidth: collapsed ? '32px' : '180px', // Increased max-width for visibility
                        height: 'auto',
                        maxHeight: collapsed ? 'auto' : '56px', // Explicitly increased height constraint
                        transition: 'all 0.3s',
                        // If collapsed, maybe hide logo or make it smaller?
                        // If toggle is here, it might overlap.
                        // Let's hide logo if collapsed to show toggle, OR show toggle below logo?
                        // User said "Top Right".
                        // In collapsed mode (72px), standard is usually just the icon.
                        opacity: (collapsed && !isMobile) ? 0 : 1 // Hide logo in collapsed desktop to show toggle clearly? Or keep both stacked?
                    }}
                />

                {/* Toggle Button - Top Right / Mobile Close */}
                <IconButton
                    onClick={isMobile ? onClose : handleCollapse}
                    size="small"
                    sx={{
                        position: collapsed && !isMobile ? 'static' : 'absolute', // Static if collapsed desktop (centered replacement)
                        right: collapsed && !isMobile ? 'auto' : 8,
                        top: collapsed && !isMobile ? 'auto' : '50%',
                        transform: collapsed && !isMobile ? 'none' : 'translateY(-50%)',
                        color: '#999',
                        '&:hover': { color: '#333', bgcolor: 'rgba(0,0,0,0.04)' },
                        display: 'flex'
                    }}
                >
                    {isMobile ? <CloseIcon /> : (collapsed ? <KeyboardArrowRightIcon /> : <KeyboardArrowLeftIcon />)}
                </IconButton>
            </Box>

            {/* Navigation */}
            <Box sx={{
                flex: 1,
                overflowY: 'auto',
                overflowX: 'hidden',
                py: 2,
                // Hide scrollbar for professional look
                scrollbarWidth: 'none', // Firefox
                '&::-webkit-scrollbar': {
                    display: 'none' // Chrome, Safari, Edge
                }
            }}>
                {activeGroups.map((group, index) => (
                    <MenuGroup key={index} title={group.title} items={group.items} />
                ))}
            </Box>

            {/* Footer - Version */}
            {
                !collapsed && (
                    <Box sx={{
                        p: 2,
                        borderTop: '1px solid #f0f0f0',
                        textAlign: 'center',
                        mt: 1
                    }}>
                        <Typography sx={{ fontSize: '10px', color: '#CCC', fontWeight: 500 }}>
                            Vó Chica v3.0
                        </Typography>
                    </Box>
                )
            }
        </Box >
    );

    return (
        <Drawer
            variant={isMobile ? "temporary" : "permanent"}
            open={isMobile ? open : true}
            onClose={onClose}
            sx={{
                width: sidebarWidth,
                flexShrink: 0,
                whiteSpace: 'nowrap',
                boxSizing: 'border-box',
                '& .MuiDrawer-paper': {
                    width: sidebarWidth,
                    borderRight: '1px solid',
                    borderColor: 'divider',
                    transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    overflowX: 'hidden',
                    boxShadow: 'none',
                    bgcolor: theme.palette.background.paper,
                    backgroundImage: 'none'
                },
            }}
        >
            {sidebarContent}
        </Drawer>
    );
}
